import React, { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { Sparkles, Download, Link2, Flame, Trophy, Zap, Target } from "lucide-react";

/** Spotify-Wrapped style weekly recap card. Shows 7-day summary + share (PNG / public link). */
export default function WeeklyWrappedCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const cardRef = useRef(null);

  useEffect(() => {
    api.get("/analytics/weekly")
      .then((r) => setData(r.data))
      .catch((err) => console.error("weekly load failed:", err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!data || data.count === 0) {
    return (
      <Card className="p-6 rounded-md border-stone-200 shadow-none" data-testid="weekly-wrapped-empty">
        <div className="flex items-center gap-3">
          <Sparkles className="h-5 w-5 text-orange-500" />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">Weekly Wrapped</div>
            <div className="text-sm text-stone-700">No attempts this week — take a mock to unlock your recap card.</div>
          </div>
        </div>
      </Card>
    );
  }

  const publicUrl = user?.username
    ? `${window.location.origin}/w/${user.username}`
    : null;

  const downloadPng = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#0c0a09",
      });
      const link = document.createElement("a");
      link.download = `exambro-week-${new Date().toISOString().slice(0, 10)}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Card saved to downloads");
    } catch (err) {
      console.error("png export failed:", err);
      toast.error("Could not export image");
    }
  };

  const copyLink = async () => {
    if (!publicUrl) {
      toast.error("Set a username in Profile to share");
      return;
    }
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Public link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <Card className="p-6 rounded-md border-stone-200 shadow-none" data-testid="weekly-wrapped">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">
            Your week on ExamBro
          </div>
          <h2 className="font-heading text-2xl font-semibold mt-1">Weekly Wrapped</h2>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={copyLink}
            data-testid="wrapped-copy-link"
            className="border-stone-300"
          >
            <Link2 className="h-3.5 w-3.5 mr-1" /> Copy link
          </Button>
          <Button
            size="sm"
            onClick={downloadPng}
            data-testid="wrapped-download-png"
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            <Download className="h-3.5 w-3.5 mr-1" /> PNG
          </Button>
        </div>
      </div>

      {/* The exportable card */}
      <div
        ref={cardRef}
        className="rounded-lg p-8 text-white relative overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #0c0a09 0%, #1c1917 45%, #7c2d12 100%)",
        }}
      >
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, #f97316 0.5px, transparent 1px), radial-gradient(circle at 80% 70%, #f97316 0.5px, transparent 1px)",
            backgroundSize: "60px 60px, 40px 40px",
          }}
        />
        <div className="relative">
          <div className="flex items-center justify-between mb-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-400">
              ExamBro · Weekly Wrapped
            </div>
            <div className="text-[10px] text-stone-400 font-mono">
              {new Date(data.week_start).toLocaleDateString()} —{" "}
              {new Date(data.week_end).toLocaleDateString()}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <MetricLarge label="Mocks" value={data.count} icon={Trophy} accent="#f97316" />
            <MetricLarge label="Focus time" value={`${data.total_focus_minutes}m`} icon={Zap} accent="#fb923c" />
            <MetricLarge label="Avg accuracy" value={`${data.avg_accuracy}%`} icon={Target} accent="#10b981" />
            <MetricLarge label="Study days" value={data.unique_days} icon={Flame} accent="#e11d48" />
          </div>

          {data.best_attempt && (
            <div className="mt-6 pt-6 border-t border-stone-800">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400 mb-1">
                Best mock this week
              </div>
              <div className="flex items-baseline justify-between">
                <div className="font-heading text-lg font-semibold truncate max-w-[70%]">
                  {data.best_attempt.exam_title}
                </div>
                <div className="font-heading text-3xl font-bold tabular-nums text-orange-500">
                  {data.best_attempt.score_pct}%
                </div>
              </div>
            </div>
          )}

          {data.best_section && (
            <div className="mt-4">
              <Badge className="bg-emerald-900/40 text-emerald-300 border-emerald-800 font-mono text-[10px] uppercase tracking-[0.2em]">
                Sharpest section · {data.best_section}
              </Badge>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function MetricLarge({ label, value, icon: Icon, accent }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4" style={{ color: accent }} />
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400">
          {label}
        </span>
      </div>
      <div className="font-heading text-4xl font-bold tabular-nums" style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}
